import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MapPin, Globe2, Phone } from "lucide-react";

/**
 * Input con autocomplete del directorio de hoteles.
 *
 * - `value` / `onChange`: sincronizados con el form padre.
 * - `onApply(hit)`: se llama al elegir un resultado. Recibe {name, address,
 *   website, city, country}. El padre puede usarlo para autocompletar la
 *   dirección y la web automáticamente.
 * - `testid`: para `data-testid`. Los items del dropdown usan
 *   `${testid}-item-{idx}` y el input el propio `testid`.
 */
export function HotelNameAutocomplete({ value, onChange, onApply, onBlur, placeholder, testid = "hotel-name-autocomplete" }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const debRef = useRef(null);
  const wrapRef = useRef(null);

  // Sync externo
  useEffect(() => { setQ(value || ""); }, [value]);

  // Cerrar al click fuera
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = async (term) => {
    if (!term || term.trim().length < 2) { setItems([]); return; }
    try {
      const { data } = await api.get(`/hotels/search`, { params: { q: term, limit: 8 } });
      setItems(data?.items || []);
    } catch { setItems([]); }
  };

  const onType = (v) => {
    setQ(v);
    onChange && onChange(v);
    setOpen(true);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => search(v), 220);
  };

  const applyHit = (hit) => {
    setOpen(false);
    onApply && onApply(hit);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        className="h-9"
        value={q}
        onChange={(e) => onType(e.target.value)}
        onFocus={() => { if (items.length) setOpen(true); }}
        onBlur={(e) => {
          // Damos 200ms para que se procese el click en un item del dropdown
          // (ese click reemplaza el nombre y no queremos disparar Google).
          if (onBlur) setTimeout(() => onBlur(e.target.value), 200);
        }}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-72 overflow-auto rounded-md border bg-popover shadow-lg" data-testid={`${testid}-dropdown`}>
          {items.map((h, i) => (
            <button
              type="button"
              key={h.name}
              onClick={() => applyHit(h)}
              className="flex w-full items-start gap-2 border-b px-3 py-2 text-left text-xs hover:bg-accent last:border-0"
              data-testid={`${testid}-item-${i}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">{h.name}</div>
                {h.address && (
                  <div className="mt-0.5 flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" /><span className="truncate">{h.address}</span></div>
                )}
                {h.website && (
                  <div className="mt-0.5 flex items-center gap-1 text-muted-foreground"><Globe2 className="h-3 w-3" /><span className="truncate">{h.website.replace(/^https?:\/\//, "")}</span></div>
                )}
                {h.phone && (
                  <div className="mt-0.5 flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" /><span className="truncate">{h.phone}</span></div>
                )}
              </div>
              {typeof h.usage_count === "number" && h.usage_count > 1 && (
                <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">×{h.usage_count}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
